import React, { useRef, useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ButtonIcon } from '@companycam/slab-web';

// Docs for this live here: https://web.archive.org/web/20230701010714mp_/https://react-google-maps-api-docs.netlify.app/#googlemap
import { GoogleMap, OverlayView } from '@react-google-maps/api';

import useShowcaseTableData from 'components/showcase/hooks/useShowcaseTableData';
import { isLightColor } from 'helpers/colorUtils';
import { translate } from 'javascripts/translations/i18n';

const FALLBACK_CENTER = {
  lat: 40.8136,
  lng: -96.7026,
};
// defined in the google maps style editor (cc-trusty-dev)
const TRUSTY_MAP_STYLE_ID = 'af7714c279c558e7';

const Pin = styled.div<{ backgroundColor?: string }>`
  width: 20px;
  height: 20px;
  background-color: ${({ backgroundColor }) => backgroundColor || '#142334'};
  border-radius: 100%;
  border-width: 1.5px;
  border-style: solid;
  border-color: ${({ backgroundColor }) =>
    backgroundColor && isLightColor(backgroundColor) ? '#142334' : 'white'};
  box-shadow: 0 var(--cc_size_depth_1_y) var(--cc_size_depth_1_blur);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.i<{ isLightBackground: boolean }>`
  color: ${({ isLightBackground }) =>
    isLightBackground ? '#142334' : 'white'};
  font-size: var(--cc_size_text_xxs);
  right: 0.5px;
  top: 0.5px;
`;

const ControlsContainer = styled.div`
  position: absolute;
  right: var(--cc_size_spacing_m);
  top: var(--cc_size_spacing_m);
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_s);
  z-index: 1;
`;

const ControlButton = styled(ButtonIcon)`
  background-color: var(--cc_color_background_1) !important;
  transition: background-color 0.2s;
  box-shadow: 0 var(--cc_size_depth_2_y) var(--cc_size_depth_2_blur)
    var(--cc_color_depth_2) !important;

  &:hover {
    background-color: var(--cc_color_background_2) !important;
  }
`;

const useCurrentPosition = () => {
  const [defaultCenter, setDefaultCenter] = useState(FALLBACK_CENTER);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setDefaultCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setIsReady(true);
        },
        () => {
          // If geolocation fails, keep fallback center
          setDefaultCenter(FALLBACK_CENTER);
          setIsReady(true);
        },
      );
    }
    setIsReady(true);
  }, []);

  return { defaultCenter, isDefaultCenterReady: isReady };
};

type MapPreviewProps = {
  brandColor?: string;
  markerDisplayLimit?: number;
  mapOptions?: google.maps.MapOptions;
  showControls?: boolean;
};

export const MapPreview = ({
  brandColor,
  markerDisplayLimit,
  mapOptions,
  showControls = true,
}: MapPreviewProps) => {
  const { rows } = useShowcaseTableData();
  const mapRef = useRef<google.maps.Map>();
  const [mapReady, setMapReady] = useState(false);
  const { defaultCenter, isDefaultCenterReady } = useCurrentPosition();

  // Filter out projects without coordinates and take first 10
  const projectsWithCoordinates = useMemo(
    () =>
      rows
        .filter(
          (project) =>
            project.project?.coordinates?.lat &&
            project.project?.coordinates?.lon &&
            project.project.coordinates.lat.toFixed(1) !== '0.0' &&
            project.project.coordinates.lon.toFixed(1) !== '0.0' &&
            project.visible,
        )
        .slice(0, markerDisplayLimit),
    [rows, markerDisplayLimit],
  );
  useEffect(() => {
    if (projectsWithCoordinates.length > 0 && mapReady) {
      if (!mapRef.current) return;

      if (projectsWithCoordinates.length === 1) {
        mapRef.current.setCenter({
          lat: projectsWithCoordinates[0].project.coordinates.lat,
          lng: projectsWithCoordinates[0].project.coordinates.lon,
        });
      } else {
        const bounds = new google.maps.LatLngBounds();
        projectsWithCoordinates.forEach((project) => {
          bounds.extend({
            lat: project.project.coordinates.lat,
            lng: project.project.coordinates.lon,
          });
        });
        mapRef.current.fitBounds(bounds);
      }
    }
  }, [projectsWithCoordinates, mapReady, isDefaultCenterReady]);

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setMapReady(true);
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      const zoom = mapRef.current.getZoom() || 10;
      mapRef.current.setZoom(zoom + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      const zoom = mapRef.current.getZoom() || 10;
      mapRef.current.setZoom(zoom - 1);
    }
  };

  const handleResetPosition = () => {
    if (mapRef.current) {
      if (projectsWithCoordinates.length === 1) {
        mapRef.current.setCenter({
          lat: projectsWithCoordinates[0].project.coordinates.lat,
          lng: projectsWithCoordinates[0].project.coordinates.lon,
        });
      } else if (projectsWithCoordinates.length > 1) {
        const bounds = new google.maps.LatLngBounds();
        projectsWithCoordinates.forEach((project) => {
          bounds.extend({
            lat: project.project.coordinates.lat,
            lng: project.project.coordinates.lon,
          });
        });
        mapRef.current.fitBounds(bounds);
      } else {
        mapRef.current.setCenter(defaultCenter);
        mapRef.current.setZoom(10);
      }
    }
  };

  return (
    <GoogleMap
      onLoad={onMapLoad}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        zoomControl: false,
        panControl: false,
        disableDefaultUI: true,
        scrollwheel: false,
        gestureHandling: 'none',
        mapId: TRUSTY_MAP_STYLE_ID,
        ...mapOptions,
      }}
      center={defaultCenter}
      mapContainerStyle={{
        height: '100%',
        width: '100%',
        borderRadius: 'var(--cc_size_border_radius_xl)',
      }}
      zoom={10}
    >
      {projectsWithCoordinates.map((project) => (
        <OverlayView
          key={project.project.id}
          position={{
            lat: project.project.coordinates.lat,
            lng: project.project.coordinates.lon,
          }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <Pin backgroundColor={`#${brandColor}`}>
            <Icon
              className="mdi mdi-creation"
              isLightBackground={brandColor ? isLightColor(brandColor) : false}
            />
          </Pin>
        </OverlayView>
      ))}
      {showControls ? (
        <ControlsContainer>
          <ControlButton
            iconName="plus"
            onClick={handleZoomIn}
            accessibilityLabel={translate('common.zoomIn')}
          />
          <ControlButton
            iconName="minus"
            onClick={handleZoomOut}
            accessibilityLabel={translate('common.zoomOut')}
          />
          <ControlButton
            iconName="crosshairs-gps"
            onClick={handleResetPosition}
            accessibilityLabel={translate('common.resetPosition')}
          />
        </ControlsContainer>
      ) : null}
    </GoogleMap>
  );
};
